/**
 * PATH: apps/quote/src/components/builder/scope-ag-grid/lazy-aggrid-styles.tsx
 * FILE_ID: QB-AGGRID-STYLES-001
 * PURPOSE: Inline CSS for the SAM-styled AG-Grid wrapper: amber-tinted
 *          pad rows + italic ghost-text on empty pad cells. Extracted
 *          from lazy-aggrid-impl.tsx so that file stays under 300 lines.
 * WHY: The CSS belongs *inside* the lazy chunk (we don't want an extra
 *      network request for ~30 lines of rules), but mixing it inline
 *      pushed lazy-aggrid-impl past the size cap once the
 *      placeholder rules landed.
 * RELATED:
 *   - QB-AGGRID-LAZY-001 (lazy-aggrid-impl.tsx) — host
 *   - QB-AGGRID-COLUMNS-001 (scope-ag-grid-columns.ts) — toggles class
 *   - QB-AGGRID-PAD-ROWS-001 (pad-row-helpers.ts) — placeholders
 */

export default function ScopeAgGridStyles() {
  return (
    <style>{`
      .qb-aggrid-with-pads .ag-row.qb-pad-row {
        background-color: rgb(254 252 232 / 0.55); /* amber-50/55 */
      }
      .qb-aggrid-with-pads .ag-row.qb-pad-row:hover {
        background-color: rgb(254 249 195 / 0.7); /* amber-100/70 */
      }
      .dark .qb-aggrid-with-pads .ag-row.qb-pad-row {
        background-color: rgb(146 64 14 / 0.08); /* amber-800/8 */
      }
      .dark .qb-aggrid-with-pads .ag-row.qb-pad-row:hover {
        background-color: rgb(146 64 14 / 0.15);
      }
      /* SAM-style ghost text inside empty pad cells. Mirrors the
         "Mark / Type material… / Pg" hints in the reference grid.
         cellClassRules in scope-ag-grid-columns.ts toggles this
         class only on pad rows where the cell is null/empty. */
      .qb-aggrid-with-pads .ag-cell.qb-pad-placeholder {
        color: rgb(120 113 108 / 0.7); /* stone-500/70 */
        font-style: italic;
      }
      .dark .qb-aggrid-with-pads .ag-cell.qb-pad-placeholder {
        color: rgb(168 162 158 / 0.55); /* stone-400/55 */
      }
      /* SAM-style per-row action icons (Copy / Delete). Faint by
         default — only fully visible when the user hovers the row.
         Mirrors SAM's screenshot exactly. */
      .qb-aggrid-with-pads .qb-row-actions {
        opacity: 0.2;
        transition: opacity 120ms ease-out;
      }
      .qb-aggrid-with-pads .ag-row:hover .qb-row-actions,
      .qb-aggrid-with-pads .ag-row.ag-row-selected .qb-row-actions {
        opacity: 1;
      }
      /* Tighten the synthetic select + actions cells so the checkbox
         and icons sit centred in their fixed-width columns. */
      .qb-aggrid-with-pads .ag-cell.qb-select-cell,
      .qb-aggrid-with-pads .ag-cell.qb-actions-cell {
        padding-left: 4px;
        padding-right: 4px;
      }
      /* Description cells with notes: faint amber left border so
         estimators can scan the column and spot caveated rows at a
         glance without hovering. The 📝 prefix in the cell text and
         the browser tooltip remain the primary signals; this is a
         lightweight secondary indicator. See
         QB-AGGRID-NOTES-DECORATOR-001 for the data wiring. */
      .qb-aggrid-with-pads .ag-cell.qb-row-has-notes {
        border-left: 2px solid rgb(245 158 11 / 0.5); /* amber-500/50 */
      }
      .dark .qb-aggrid-with-pads .ag-cell.qb-row-has-notes {
        border-left-color: rgb(252 211 77 / 0.5); /* amber-300/50 */
      }
    `}</style>
  )
}
